/**
 * Google Meet Integration Utilities
 * Handles Google Meet creation via Google Calendar API with enhanced Gmail integration
 */

import { isGoogleConnected, initializeGoogleAPI } from './googleAuth';
import { sendGmailMessage } from './gmailUtils';
import { gapi } from 'gapi-script';

/**
 * Check if Google Meet is available (requires Google Calendar connection)
 */
export function isGoogleMeetAvailable() {
  return isGoogleConnected() && window.gapi && window.gapi.client && window.gapi.client.calendar;
}

/**
 * Check if Gmail is available
 */
export function isGmailAvailable() {
  return isGoogleConnected() && window.gapi && window.gapi.client && window.gapi.client.gmail;
}

/**
 * Initialize Google API if needed
 */
async function ensureGoogleAPIReady() {
  if (!window.gapi || !window.gapi.client) {
    await initializeGoogleAPI();
  }
}

/**
 * Create a Google Meet meeting by creating a Calendar event with conference data
 */
export async function createGoogleMeet(meetingDetails) {
  try {
    // ✅ Validate meetingDetails object before using
    if (!meetingDetails || typeof meetingDetails !== 'object') {
      throw new Error('Invalid meeting details provided');
    }

    // ✅ Check title and startTime exist with proper validation
    if (!meetingDetails.title || typeof meetingDetails.title !== 'string') {
      throw new Error('Meeting title is required and must be a string');
    }

    // Ensure Google API is ready
    await ensureGoogleAPIReady();
    
    if (!isGoogleMeetAvailable()) {
      throw new Error('Google Calendar not connected or API not available');
    }

    const startTime = meetingDetails.startTime || new Date().toISOString();
    const endTime = meetingDetails.endTime || new Date(Date.now() + 60 * 60 * 1000).toISOString();

    // ✅ Validate startTime is a valid date
    const startDate = new Date(startTime);
    const endDate = new Date(endTime);
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      throw new Error('Invalid start or end time provided');
    }

    const event = {
      summary: meetingDetails.title || 'Google Meet Meeting',
      description: meetingDetails.description || '',
      start: {
        dateTime: startTime,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      end: {
        dateTime: endTime,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      conferenceData: {
        createRequest: {
          requestId: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          conferenceSolutionKey: {
            type: "hangoutsMeet"
          }
        }
      }
    };

    // ✅ Wrap calendar API insert call with error logging + user-friendly message
    let response;
    try {
      response = await window.gapi.client.calendar.events.insert({
        calendarId: 'primary',
        resource: event,
        conferenceDataVersion: 1
      });
    } catch (apiError) {
      console.error('Google Calendar API error during event creation:', apiError);
      throw new Error(`Failed to create calendar event: ${apiError.message || 'Unknown calendar API error'}`);
    }

    if (!response || !response.result) {
      throw new Error('Invalid response from Google Calendar API');
    }

    const createdEvent = response.result;
    
    // Extract Google Meet link
    let meetLink = null;
    // ✅ Add fallback for missing conferenceData.entryPoints
    if (createdEvent.conferenceData && 
        createdEvent.conferenceData.entryPoints && 
        Array.isArray(createdEvent.conferenceData.entryPoints)) {
      const videoEntry = createdEvent.conferenceData.entryPoints.find(ep => ep.entryPointType === 'video');
      if (videoEntry && videoEntry.uri) {
        meetLink = videoEntry.uri;
      }
    }

    // ✅ Warn if no meet link was generated
    if (!meetLink) {
      console.warn('Google Meet link was not generated for the calendar event');
    }

    return {
      success: true,
      meetingId: createdEvent.id,
      meetLink: meetLink,
      event: createdEvent
    };
  } catch (error) {
    console.error('Error creating Google Meet:', error);
    return {
      success: false,
      error: error.message || 'Failed to create Google Meet event'
    };
  }
}

/**
 * Create Google Meet with email invitations
 */
export async function createGoogleMeetWithInvites(meetingDetails, attendees = []) {
  try {
    // ✅ Validate meetingDetails object before using
    if (!meetingDetails || typeof meetingDetails !== 'object') {
      throw new Error('Invalid meeting details provided');
    }

    // ✅ Guard against invalid invitee email strings
    if (!Array.isArray(attendees)) {
      throw new Error('Attendees must be provided as an array');
    }

    const validAttendees = attendees.filter(email => {
      if (typeof email !== 'string' || !email.includes('@') || email.length < 5) {
        console.warn(`Invalid email address skipped: ${email}`);
        return false;
      }
      return true;
    });

    if (validAttendees.length !== attendees.length) {
      console.warn(`${attendees.length - validAttendees.length} invalid email addresses were filtered out`);
    }

    // Ensure Google API is ready
    await ensureGoogleAPIReady();
    
    if (!isGoogleMeetAvailable()) {
      throw new Error('Google Calendar not connected or API not available');
    }

    const startTime = meetingDetails.startTime || new Date().toISOString();
    const endTime = meetingDetails.endTime || new Date(Date.now() + 60 * 60 * 1000).toISOString();

    const event = {
      summary: meetingDetails.title || 'Google Meet Meeting',
      description: meetingDetails.description || '',
      start: {
        dateTime: startTime,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      end: {
        dateTime: endTime,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      attendees: validAttendees.map(email => ({ email: email.trim() })),
      conferenceData: {
        createRequest: {
          requestId: 'hangoutsMeet-' + Math.random().toString(36).substring(2, 15),
          conferenceSolutionKey: { type: 'hangoutsMeet' }
        }
      },
      reminders: {
        useDefault: false,
        overrides: [
          { method: 'email', minutes: 1440 }, // 24 hours
          { method: 'popup', minutes: 10 }
        ]
      }
    };

    // ✅ Wrap calendar API insert call with error logging + user-friendly message
    let response;
    try {
      response = await gapi.client.calendar.events.insert({
        calendarId: 'primary',
        resource: event,
        conferenceDataVersion: 1,
        sendUpdates: 'all' // Send email invitations to all attendees
      });
    } catch (apiError) {
      console.error('Google Calendar API error during event creation with invites:', apiError);
      throw new Error(`Failed to create calendar event with invitations: ${apiError.message || 'Unknown calendar API error'}`);
    }

    if (!response || !response.result) {
      throw new Error('Invalid response from Google Calendar API');
    }

    const createdEvent = response.result;
    let meetLink = null;

    // ✅ Add fallback for missing conferenceData.entryPoints with enhanced validation
    if (createdEvent.conferenceData && 
        createdEvent.conferenceData.entryPoints && 
        Array.isArray(createdEvent.conferenceData.entryPoints)) {
      const videoEntry = createdEvent.conferenceData.entryPoints.find(
        entry => entry && entry.entryPointType === 'video' && entry.uri
      );
      if (videoEntry) {
        meetLink = videoEntry.uri;
      }
    }

    // ✅ Warn if no meet link was generated
    if (!meetLink) {
      console.warn('Google Meet link was not generated for the calendar event with invitations');
    }

    return {
      success: true,
      meetingId: createdEvent.id,
      meetLink: meetLink,
      event: createdEvent,
      invitesSent: validAttendees.length,
      originalAttendeesCount: attendees.length
    };
  } catch (error) {
    console.error('Error creating Google Meet with invites:', error);
    return {
      success: false,
      error: error.message || 'Failed to create Google Meet event with invitations'
    };
  }
}

/**
 * Format meeting details for Google Meet creation
 */
export function formatMeetingForGoogleMeet(taskDetails) {
  const startDateTime = taskDetails.date && taskDetails.time 
    ? `${taskDetails.date}T${taskDetails.time}:00`
    : new Date().toISOString();

  // Default to 1 hour duration if not specified
  const duration = taskDetails.duration || 60; // minutes
  const endDateTime = new Date(new Date(startDateTime).getTime() + duration * 60 * 1000).toISOString();

  return {
    title: taskDetails.title || 'Google Meet Meeting',
    description: taskDetails.details || '',
    startTime: startDateTime,
    endTime: endDateTime
  };
}

/**
 * Get Google Meet connection status
 */
export function getGoogleMeetStatus() {
  return {
    connected: isGoogleConnected(),
    calendarAvailable: isGoogleMeetAvailable(),
    gmailAvailable: isGmailAvailable()
  };
}

/**
 * Get comprehensive Google services status
 */
export function getGoogleServicesStatus() {
  const connected = isGoogleConnected();
  return {
    connected,
    calendar: connected && isGoogleMeetAvailable(),
    gmail: connected && isGmailAvailable(),
    meet: connected && isGoogleMeetAvailable()
  };
}

/**
 * Send Google Meet invitation via Gmail
 */
export async function sendGoogleMeetInvitation(meetingLink, invitationDetails) {
  try {
    if (!isGmailAvailable()) {
      throw new Error('Gmail not connected');
    }

    const {
      to = [],
      subject = 'Google Meet Invitation',
      title = 'Meeting',
      description = '',
      startTime = '',
      endTime = '',
      organizer = 'Meeting Organizer'
    } = invitationDetails;

    // Format start and end times for display
    const formatTime = (timeString) => {
      if (!timeString) return 'TBD';
      try {
        return new Date(timeString).toLocaleString();
      } catch {
        return timeString;
      }
    };

    const emailBody = `
Dear Participant,

You're invited to join a Google Meet meeting:

📅 Meeting: ${title}
🕐 Start Time: ${formatTime(startTime)}
🕑 End Time: ${formatTime(endTime)}
👤 Organizer: ${organizer}

${description ? `📝 Description: ${description}\n` : ''}
🔗 Join Meeting: ${meetingLink}

To join the meeting:
1. Click the meeting link above
2. Or copy and paste the link into your browser
3. Allow camera and microphone permissions when prompted

Meeting Details:
- Meeting Link: ${meetingLink}
- Meeting ID can be found in the URL

Best regards,
${organizer}

---
This meeting was created using AI Scheduler Pro
    `.trim();

    // Send to each recipient
    const results = [];
    for (const email of to) {
      try {
        const result = await sendGmailMessage({
          to: email,
          subject: subject,
          body: emailBody,
          isHtml: false
        });
        results.push({ email, success: true, result });
      } catch (error) {
        console.error(`Failed to send invitation to ${email}:`, error);
        results.push({ email, success: false, error: error.message });
      }
    }

    return {
      success: true,
      invitationsSent: results.filter(r => r.success).length,
      totalRecipients: to.length,
      results
    };

  } catch (error) {
    console.error('Failed to send Google Meet invitations:', error);
    throw error;
  }
}

/**
 * Create Google Meet with automatic email invitations
 */
export async function createGoogleMeetWithInvitations(meetingDetails, invitationList = []) {
  try {
    // Create the Google Meet first
    const meetResult = await createGoogleMeet(meetingDetails);
    
    if (!meetResult.success || !meetResult.meetLink) {
      throw new Error('Failed to create Google Meet');
    }

    // Send invitations if recipients provided
    let invitationResults = null;
    if (invitationList.length > 0 && isGmailAvailable()) {
      try {
        invitationResults = await sendGoogleMeetInvitation(meetResult.meetLink, {
          to: invitationList,
          title: meetingDetails.title || 'Google Meet Meeting',
          description: meetingDetails.description || '',
          startTime: meetingDetails.startTime,
          endTime: meetingDetails.endTime,
          organizer: meetingDetails.organizer || 'AI Scheduler'
        });
      } catch (invitationError) {
        console.warn('Failed to send invitations:', invitationError);
        // Don't fail the entire operation if invitations fail
      }
    }

    return {
      ...meetResult,
      invitations: invitationResults
    };

  } catch (error) {
    console.error('Failed to create Google Meet with invitations:', error);
    throw error;
  }
}
